import { Box, CircularProgress } from "@mui/material";

// Date helpers now live in src/lib/dates.js. Re-exported here so existing
// imports keep working; new code should import from "lib/dates" directly.
export {
  formatDate,
  formatMonthYear,
  formatYear,
  formatLocalDate,
  displayDate,
  toDateString,
  todayDateString,
  monthStartString,
  generateMonthYearOptions,
} from "../lib/dates";

// Person names (first/last): letters, numbers, spaces, and  . , ' -
const VALID_PERSON_NAME_REGEX = /^[a-zA-Z0-9 .,'-]*$/;
export const PERSON_NAME_ALLOWED = "letters, numbers, spaces, and . , ' -";

// Company / display names: letters, numbers, spaces, and  , . : ; " - _ # & / '
const VALID_COMPANY_NAME_REGEX = /^[a-zA-Z0-9 ,.:;"\-_#&/'\u2018\u2019\u201C\u201D]*$/;
export const COMPANY_NAME_ALLOWED =
  "letters, numbers, spaces, and , . : ; \" - _ # & / ' (including curly quotes)";

export const shorten = (str, chars) => {
  if (str?.length <= chars) return str;
  const start = str?.slice(0, chars); // keep enough for "..." + last 3 chars
  return start + "...";
};

export const formatAmount = (value) => {
  if (value == null || value === "") return "-";
  const amount = Number(value) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
};
export const formatPercent = (value) => {
  if (value == null || value === "") return "-";
  const percentNumber = Number(value) * 100;
  const amount = percentNumber.toFixed(2);
  return `${amount}%`;
};

const toPhoneDigits = (value) => {
  let digits = String(value).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  if (digits.length > 10) {
    digits = digits.slice(-10);
  }
  return digits;
};

const formatPhoneInternal = (value, { allowPartial = false } = {}) => {
  if (!value) return "";

  const digits = toPhoneDigits(value);
  const length = digits.length;

  if (length === 0) return "";

  if (!allowPartial && length < 10) {
    return String(value);
  }

  if (allowPartial) {
    if (length < 4) return `(${digits}`;
    if (length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const formatPhone = (value) => formatPhoneInternal(value, { allowPartial: false });

export const formatPhoneNumber = (value) =>
  formatPhoneInternal(value, { allowPartial: true });

export const formatSSN = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 9);
  const length = digits.length;

  if (length === 0) return "";
  if (length < 4) return digits;
  if (length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

export const formatZip = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 9);
  if (digits.length > 5) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return digits;
};

// Digits only, capped at 9 — the stored form of a zip. formatZip is display.
export const stripZip = (value) =>
  String(value ?? "").replace(/\D/g, "").slice(0, 9);

export const validatePassword = (password) => {
  const value = password || "";
  const invalidMatches = value.match(/[^A-Za-z0-9@$!%*&#]/g);
  const invalidChars = invalidMatches ? [...new Set(invalidMatches)] : [];
  return {
    length: value.length >= 8,
    special: /[@$!%*&#]/.test(value),
    number: /[0-9]/.test(value),
    upperCase: /[A-Z]/.test(value),
    noInvalidChars: invalidChars.length === 0,
    invalidChar: invalidChars.length > 0 ? invalidChars.join(", ") : null,
    invalidChars: invalidChars
  }
}

export const validateWebsite = (website) => {
  const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
  return regex.test(website);
};

export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const validatePersonNameChars = (value) => {
  if (!value) return true;
  return VALID_PERSON_NAME_REGEX.test(value);
};

export const validateCompanyNameChars = (value) => {
  if (!value) return true;
  return VALID_COMPANY_NAME_REGEX.test(value);
};
/**
 * The backend's payload allowlist: alphanumeric, whitespace, and
 * , . : ; " - _ # & / '
 *
 * Characters outside it are rejected server-side with 105-01 — they are
 * hazardous in URLs and downstream files, so this is a REJECT rule rather
 * than something sanitizeText silently strips. The user typed them; tell them.
 *
 * CURLY quotes are accepted here but NOT by the backend. This validator runs
 * on the raw field, BEFORE sanitizeText normalizes them to their straight
 * equivalents — so rejecting them would fail input that would have been
 * perfectly valid by the time it was sent.
 */
const VALID_PAYLOAD_TEXT_REGEX =
  /^[a-zA-Z0-9\s,.:;"'\-_#&/\u2018\u2019\u201C\u201D]*$/;
export const PAYLOAD_TEXT_ALLOWED =
  "letters, numbers, spaces, and , . : ; \" ' - _ # & /";

export const validatePayloadTextChars = (value) => {
  if (!value) return true;
  return VALID_PAYLOAD_TEXT_REGEX.test(value);
};
// ---------------------------------------------------------------------------
// Text sanitizing
//
// Google Places autocomplete returns display text containing typographic
// characters — a bullet separator between the main and secondary lines, curly
// quotes, en dashes. They render fine in the browser and fail downstream,
// where NACHA files are fixed-width ASCII and carrier submissions are
// similarly unforgiving.
//
// Mapped to ASCII equivalents first, so "Smith–Jones" becomes "Smith-Jones"
// rather than "SmithJones".
// ---------------------------------------------------------------------------

const TYPOGRAPHIC = {
  "\u2022": " ",   // • bullet
  "\u00B7": " ",   // · middle dot
  "\u2018": "'",
  "\u2019": "'",
  "\u201C": '"',
  "\u201D": '"',
  "\u2013": "-",
  "\u2014": "-",
  "\u2026": "...",
  "\u00A0": " ",   // non-breaking space
};

export const sanitizeText = (value) => {
  if (value == null) return value;

  return String(value)
    .replace(
      /[\u2022\u00B7\u2018\u2019\u201C\u201D\u2013\u2014\u2026\u00A0]/g,
      (c) => TYPOGRAPHIC[c],
    )
    // Decompose accents so José -> Jose rather than Jos
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};
export const LoadingOverlay = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // backgroundColor: "rgba(255, 255, 255, 0.75)",
        zIndex: 2000,
        backdropFilter: "blur(0px)",
        pointerEvents: "all",
        width: "100%",
        height: "100%",
      }}
    >
      <CircularProgress sx={{ color: "#6C63FF" }} />
    </Box>
  );
};

// ----------------------------------------------------------------------------
// Storage helpers
// ----------------------------------------------------------------------------

const getStorage = () => (typeof window === "undefined" ? null : window.localStorage);

export const storage = {
  readJSON: (key) => {
    const storageRef = getStorage();
    if (!storageRef) return null;
    const raw = storageRef.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error(`Failed to parse JSON from localStorage key "${key}":`, error);
      return null;
    }
  },

  writeJSON: (key, value) => {
    const storageRef = getStorage();
    if (!storageRef) return;
    try {
      storageRef.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to stringify JSON for localStorage key "${key}":`, error);
    }
  },

  read: (key) => {
    const storageRef = getStorage();
    if (!storageRef) return null;
    return storageRef.getItem(key);
  },

  write: (key, value) => {
    const storageRef = getStorage();
    if (!storageRef) return;
    storageRef.setItem(key, value);
  },

  remove: (key) => {
    const storageRef = getStorage();
    if (!storageRef) return;
    storageRef.removeItem(key);
  },

  clearBySuffix: (suffix) => {
    const storageRef = getStorage();
    if (!storageRef) return;
    const keysToRemove = [];
    for (let i = 0; i < storageRef.length; i += 1) {
      const key = storageRef.key(i);
      if (key && key.endsWith(suffix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storageRef.removeItem(key));
  }
};